import React, { useState, useEffect } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RTooltip,
  ResponsiveContainer,
} from 'recharts';
import { CheckCircle2, Loader2 } from 'lucide-react';
import {
  getActivityTimeline,
  getAppUsage,
  getBehaviorPatterns,
} from '../services/api';
import { PageHeader } from '../components/ui/page-header';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';

const CHART_COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--success))',
  'hsl(var(--warning))',
  'hsl(var(--destructive))',
  'hsl(var(--muted-foreground))',
];

function AnalyticsPage() {
  const [loading, setLoading] = useState(true);
  const [appUsage, setAppUsage] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [, appRes] = await Promise.all([
        getActivityTimeline(168),
        getAppUsage(168),
        getBehaviorPatterns(168).catch(() => ({ data: { data: null } })),
      ]);
      setAppUsage(appRes.data.data || []);
    } catch (err) {
      console.error('Failed to load analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const getProductivityMetrics = () => {
    const productive = ['excel.exe', 'word.exe', 'powerpoint.exe', 'Code.exe'];
    const communication = ['teams.exe', 'outlook.exe', 'slack.exe'];

    const prodTime = appUsage
      .filter((a) =>
        productive.some((p) => a.application.toLowerCase().includes(p.toLowerCase()))
      )
      .reduce((sum, a) => sum + a.duration, 0);
    const commTime = appUsage
      .filter((a) =>
        communication.some((c) => a.application.toLowerCase().includes(c.toLowerCase()))
      )
      .reduce((sum, a) => sum + a.duration, 0);
    const totalTime = appUsage.reduce((sum, a) => sum + a.duration, 0);

    return {
      productive: Math.round((prodTime / totalTime) * 100) || 0,
      communication: Math.round((commTime / totalTime) * 100) || 0,
      other: Math.round(((totalTime - prodTime - commTime) / totalTime) * 100) || 0,
    };
  };

  const metrics = getProductivityMetrics();
  const productivityData = [
    { name: 'Productive', value: metrics.productive },
    { name: 'Communication', value: metrics.communication },
    { name: 'Other', value: metrics.other },
  ];

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6">
      <PageHeader
        title="Analytics & Insights"
        description="Advanced analytics and productivity metrics"
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Productivity Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={productivityData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${entry.value}%`}
                  outerRadius={90}
                  dataKey="value"
                  stroke="hsl(var(--card))"
                >
                  {productivityData.map((_, index) => (
                    <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <RTooltip
                  contentStyle={{
                    background: 'hsl(var(--popover))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 8,
                    color: 'hsl(var(--foreground))',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Productivity Score</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-6">
              <span className="text-7xl font-bold tracking-tight text-primary tabular-nums">
                {metrics.productive}%
              </span>
              <span className="mt-2 text-sm text-muted-foreground">
                Based on application usage patterns
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Detected Patterns</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Alert variant="success">
            <CheckCircle2 />
            <AlertDescription>
              No suspicious patterns detected. Normal behavior.
            </AlertDescription>
          </Alert>
          <p className="text-xs text-muted-foreground">
            Advanced pattern detection analyzes clipboard, USB, and network activities for
            suspicious correlations.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default AnalyticsPage;
